import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useCurrentSpace } from "@/hooks/useCurrentSpace.jsx";
import { getNativeAdAccounts } from "@/services/adAccounts.js";

export const DashboardContext = createContext(null)

export function DashboardProvider({ children }) {

    const { space } = useCurrentSpace()

    const scrollContainerRef = useRef(null) // we use this to scroll to top of the page when needed, that way we have global access to it.

    /* Integrations */
    const [integrations, setIntegrations] = useState([])
    const updateIntegrationsState = (integrationsToUpdateArray) => {
        setIntegrations([...integrations, ...integrationsToUpdateArray])
    }
    const updateExistingIntegrationState = (integrationId, newData) => {
        setIntegrations(integrations.map(i => i._id === integrationId ? newData : i))
    }
    const removeIntegrationFromState = (integrationId) => {
        setIntegrations(integrations.filter(i => i._id !== integrationId))
    }

    /* Notifications */ // TODO: remove this
    const [notifications, setNotifications] = useState([])
    const addNotifications = (newNotifications) => {
        setNotifications([...newNotifications, ...notifications])
    }
    const removeNotification = (notificationId) => {
        setNotifications(notifications.filter(n => n._id !== notificationId))
    }

    /* Creatives */
    const [creativesMap, setCreativesMap] = useState({})
    const [creativesLoadingMap, setCreativesLoadingMap] = useState({})
    const getAllCreativesFromState = () => Object.values(creativesMap)
    const getAllTopLevelCreativesFromState = () => getAllCreativesFromState().filter(c => !c.folder)
    const getCreativeFromState = (creativeId) => creativesMap[creativeId]
    const updateCreativeStateById = (creativeId, newData) => {
        setCreativesMap(prev => {
            const newState = { ...prev }
            const prevCreativeState = prev[creativeId] || {}
            newState[creativeId] = { ...prevCreativeState, ...newData }
            return newState
        })
    }
    const removeManyCreativesFromState = (creativeIds) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        for (const creativeId of creativeIds) {
            delete newSate[creativeId]
        }
        setCreativesMap(prev => ({ ...prev, ...newSate }))
    }
    const removeCreativeFromState = (creativeId) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        delete newSate[creativeId]
        setCreativesMap(prev => ({ ...prev, ...newSate }))
    }
    const updateCreativeState = (creatives) => {
        const newSate = JSON.parse(JSON.stringify(creativesMap))
        for (const creative of creatives) {
            newSate[creative._id] = creative
        }
        setCreativesMap(prev => ({ ...prev, ...newSate }))
    }

    const updateCreativeLoadingStateById = (creativeId, isLoading) => {
        setCreativesLoadingMap(prev => ({ ...prev, [creativeId]: isLoading }))
    }

    const getCreativeLoadingStateFromState = (creativeId) => creativesLoadingMap[creativeId]

    const getCreativesOfFolderFromState = (folderId) => getAllCreativesFromState().filter(c => c.folder === folderId)
    const getAllCreativesForAdAccount = (adAccountId) => getAllCreativesFromState().filter(c => c.adAccountId === adAccountId)
    const getAllPropertiesForThisAdAccount = (adAccountId) => {

        const allCreatives = getAllCreativesForAdAccount(adAccountId)
        const allPropertiesMap = {}
        const tagMap = {}

        for (const creative of allCreatives) {
            if (creative.properties) {
                for (const property of creative.properties) {
                    const propertyLabel = property.label?.toLowerCase()
                    if (propertyLabel) {
                        if (!allPropertiesMap[propertyLabel]) {
                            allPropertiesMap[propertyLabel] = { label: property.label, tags: [] }
                        }
                        if (property.tags) {
                            for (const tag of property.tags) {
                                const key = `${propertyLabel}_${tag.value}`
                                if (!tagMap[key]) {
                                    tagMap[key] = true
                                    allPropertiesMap[propertyLabel].tags.push(tag)
                                }
                            }
                        }
                    }
                }
            }
        }

        return allPropertiesMap

    }

    /* Folders */
    const [foldersMap, setFoldersMap] = useState({})
    const getAllFoldersFromState = () => Object.values(foldersMap)
    const getTopLevelFoldersFromState = () => getAllFoldersFromState().filter(f => !f.parent)
    const getFolderChildrenFromState = (folderId) => getAllFoldersFromState().filter(f => f.parent === folderId)
    const getFolderFromState = (folderId) => foldersMap[folderId]
    const updateFolderStateById = (folderId, newData) => {
        const newSate = JSON.parse(JSON.stringify(foldersMap))
        newSate[folderId] = newData
        setFoldersMap(newSate)
    }
    const removeFolderFromState = (folderId) => {
        const newSate = JSON.parse(JSON.stringify(foldersMap))
        delete newSate[folderId]
        setFoldersMap(newSate)
    }
    const updateFolderState = (folders) => {
        const newSate = JSON.parse(JSON.stringify(foldersMap))
        for (const folder of folders) {
            newSate[folder._id] = folder
        }
        setFoldersMap(newSate)
    }

    /* Creative Reports */
    const [creativeReports, setCreativeReports] = useState([])
    const getCreativeReportFromState = (creativeId) => creativeReports.find(c => c._id === creativeId)
    const updateCreativeReportState = (creativeReport) => {
        const newState = creativeReports.map(c => c._id === creativeReport._id ? creativeReport : c)
        setCreativeReports(newState)
    }
    const updateManyCreativeReportsState = (reportsToUpdate) => {
        const reportsMap = new Map(reportsToUpdate.map(r => [r._id, r]))
        setCreativeReports(prev => prev.map(c => reportsMap.has(c._id) ? reportsMap.get(c._id) : c))
    }
    const addManyCreativeReportsToState = (creativeReportsToUpdate) => {
        setCreativeReports([...creativeReports, ...creativeReportsToUpdate])
    }
    const removeCreativeReportFromState = (creativeId) => {
        setCreativeReports(creativeReports.filter(c => c._id !== creativeId))
    }

    /* Report Folders (for organizing creative reports) */
    const [reportFolders, setReportFolders] = useState([])
    const getReportFolderFromState = (folderId) => reportFolders.find(f => f._id === folderId)
    const updateReportFolderState = (folder) => {
        const newState = reportFolders.map(f => f._id === folder._id ? folder : f)
        setReportFolders(newState)
    }
    const updateManyReportFoldersState = (foldersToUpdate) => {
        const foldersMap = new Map(foldersToUpdate.map(f => [f._id, f]))
        setReportFolders(prev => prev.map(f => foldersMap.has(f._id) ? foldersMap.get(f._id) : f))
    }
    const addReportFolderToState = (folder) => {
        setReportFolders([...reportFolders, folder])
    }
    const removeReportFolderFromState = (folderId) => {
        setReportFolders(reportFolders.filter(f => f._id !== folderId))
    }
    const updateReportFoldersOrder = (newFoldersArray) => {
        setReportFolders(newFoldersArray)
    }

    /* General (not sure yet where to put it) */
    const [chosenAdAccount, setChosenAdAccount] = useState(null)

    // Default the chosen ad account globally so any dashboard page (home, reports,
    // views, …) has one selected — not just the pages that used to set it. Picks the
    // space's first ad account when none is chosen, and resets when the current
    // selection doesn't belong to this space (e.g. after switching spaces).
    useEffect(() => {
        // Analytics-only accounts (e.g. TripleWhale) are never a valid choice here -
        // only a native ad account can become the globally-chosen one.
        const accounts = getNativeAdAccounts(space?.adAccounts)
        if (!accounts.length) {
            if (chosenAdAccount) {
                setChosenAdAccount(null)
            }
            return
        }
        if (!chosenAdAccount || !accounts.find(a => a.id === chosenAdAccount)) {
            setChosenAdAccount(accounts[0].id)
        }
    }, [space?._id, space?.adAccounts?.length])

    return (
        <DashboardContext.Provider
            value={{
                creativeContext: {
                    setCreativesMap, getCreativeFromState, updateCreativeState, updateCreativeStateById, getCreativesOfFolderFromState,
                    getAllCreativesFromState, removeManyCreativesFromState, getAllTopLevelCreativesFromState,
                    setFoldersMap, updateFolderStateById, getFolderFromState, removeFolderFromState, updateFolderState, getAllFoldersFromState, getTopLevelFoldersFromState, getFolderChildrenFromState, removeCreativeFromState,
                    creativeReports, getCreativeReportFromState, updateCreativeReportState, updateManyCreativeReportsState, addManyCreativeReportsToState, removeCreativeReportFromState, setCreativeReports,
                    getAllPropertiesForThisAdAccount, updateCreativeLoadingStateById, getCreativeLoadingStateFromState,
                    reportFolders, setReportFolders, getReportFolderFromState, updateReportFolderState, updateManyReportFoldersState, addReportFolderToState, removeReportFolderFromState, updateReportFoldersOrder
                },
                integrationsContext: { integrations, updateIntegrationsState, updateExistingIntegrationState, removeIntegrationFromState },
                notificationsContext: { notifications, addNotifications, removeNotification },
                adAccountsContext: { chosenAdAccount, setChosenAdAccount },
                scrollContext: { scrollContainerRef }
            }}
        >
            {children}
        </DashboardContext.Provider>
    )
}

export const useDashboardContext = () => {
    const context = useContext(DashboardContext);

    if (!context) throw new Error('useDashboardContext context must be use inside DashboardProvider');

    return context;
}

